import React from 'react'

import { useTranslation } from 'react-i18next'
import cx from 'classnames'

// MUI Imports
import {
  Typography,
  Box,
  Button
} from '@mui/material'
import {
  Navigation,
  MailOutline,
  Add
} from '@mui/icons-material'

// Component Imports
import AvatarWithTexts from '../atomic/atoms/AvatarWithTexts'
import LoadingOverlay from '../atomic/atoms/LoadingOverlay'

// Styles Imports
import styles from '../styles/mailbox.module.scss'

import { extractHtmlContent } from '../../utils/helpers'
import McPageHelpers from '../../../../pages/mc-components/McPageHelpers'
import { last } from 'lodash'

const ScrollWrapper = ({ children }) => {
  return <div className='bs-full overflow-y-auto overflow-x-hidden relative'>{children}</div>
}

const getDeepestMessage = (message) => {
  if (message.replies && message.replies.length > 0) {
    return getDeepestMessage(last(message.replies))
  }
  return message
}

const getDisplayInfo = (emailArr, t) => {
  const mainEmail = emailArr?.[0]
  const latestMessage = getDeepestMessage(mainEmail)

  let displayName, displayAddress, navRotation

  if (latestMessage?.type === 'INCOMING') {
    displayName = latestMessage.from_name || t('mailTemplate.mailbox.unknownSender')
    displayAddress = latestMessage.from_address || ''
    navRotation = '210deg'
  } else {
    displayName = latestMessage?.to?.[0]?.name || t('mailTemplate.mailbox.unknownRecipient')
    displayAddress = latestMessage?.to?.[0]?.address || ''
    navRotation = '30deg'
  }

  return { displayName, displayAddress, navRotation }
}

const MailContentList = props => {
  const {
    fetching,
    isBelowSmScreen,
    areFilteredEmailsNone,
    searchTerm,
    emailArrays,
    setDrawerOpen,
    setActiveEmailArr,
    setOpenCompose
  } = props

  const { t } = useTranslation()

  const userLocale = McPageHelpers.localeMap[localStorage.getItem('lang')] || 'en-US'

  const filteredEmailArrays = emailArrays
    .filter(
      emailArr =>
        emailArr?.[0]?.subject?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        emailArr?.[0]?.from_name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        emailArr?.[0]?.from_address?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        emailArr?.[0]?.to?.[0]?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        emailArr?.[0]?.to?.[0]?.address?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        extractHtmlContent(emailArr?.[0]?.bodyHtml)?.toLowerCase().includes(searchTerm?.toLowerCase())
    )

  return areFilteredEmailsNone
    ? (
        <div className='relative flex justify-center items-center gap-2 bg-backgroundPaper' style={{ flex: 1, height: '100%' }}>
          {fetching && <LoadingOverlay />}
          {!fetching && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  p: 4
                }}>
                  <Box
                    sx={{
                      maxWidth: 480,
                      textAlign: 'center',
                      p: 6,
                      backgroundColor: 'grey.50',
                      border: '2px dashed',
                      borderColor: 'grey.300',
                      borderRadius: 3
                    }}
                  >
                      <Box sx={{ mb: 3 }}>
                        <MailOutline
                          sx={{
                            fontSize: 64,
                            color: 'primary.main',
                            mb: 2
                          }}
                        />
                      </Box>

                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: 'text.primary'
                        }}
                      >
                        {t('mailTemplate.mailbox.list.noData')}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          mb: 4,
                          lineHeight: 1.6,
                          maxWidth: 360,
                          mx: 'auto'
                        }}
                      >
                        {t('mailTemplate.mailbox.list.noDataDescription')}
                      </Typography>

                      {setOpenCompose && (
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => setOpenCompose(true)}
                          sx={{
                            borderRadius: 2,
                            height: 40,
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 500
                          }}
                        >
                          {t('mailTemplate.mailbox.list.noDataAction')}
                        </Button>
                      )}
                  </Box>
                </Box>
          )
          }
        </div>
      )
    : (
        <div className='relative overflow-hidden' style={{ flex: 1, height: '100%' }}>
          <ScrollWrapper>
            <div className='flex flex-col'>
              {filteredEmailArrays?.map((emailArr) => {
                const { displayName, displayAddress, navRotation } = getDisplayInfo(emailArr, t)

                return (
                  <div
                    key={emailArr?.[0]?.messageId}
                    className={cx('p-2 cursor-pointer', styles.emailList, { 'bg-actionHover': true })}
                    onClick={() => {
                      setActiveEmailArr(emailArr)
                      setDrawerOpen(true)
                    }}
                  >
                    <div className='flex items-center justify-between gap-2'>
                      <div className='flex items-center gap-2 overflow-hidden'>
                        <AvatarWithTexts
                          text={displayName}
                          subText={displayAddress}
                          style={{
                            minWidth: '280px',
                            maxWidth: '280px'
                          }}
                          startAdornment={
                            <Navigation
                              color='primary'
                              sx={{
                                rotate: navRotation
                              }}
                            />
                          }
                        />
                        <div className='overflow-hidden'>
                          <Typography variant='body2' noWrap className='pl-2'>
                            {emailArr?.[0]?.subject || t('mailTemplate.mailbox.noSubject')}
                          </Typography>
                          <Typography variant='body2' noWrap color='text.disabled' className='pl-2'>
                            {extractHtmlContent(emailArr?.[0]?.bodyHtml) || '-'}
                          </Typography>
                        </div>
                      </div>
                      {!isBelowSmScreen && (
                        <div
                          className={cx('flex items-center gap-2', styles.emailInfo, {
                            [styles.show]: true
                          })}
                        >
                          <Typography variant='body2' color='text.disabled' className='whitespace-nowrap'>
                            {
                              emailArr?.[0]?.sentTimeMillis && emailArr?.[0]?.sentTimeMillis > 0
                                ? new Intl.DateTimeFormat(userLocale, {
                                  year: 'numeric',
                                  month: 'short',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }).format(new Date(emailArr?.[0]?.sentTimeMillis))
                                : t('mailTemplate.mailbox.noDate')
                            }
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </ScrollWrapper>
          {fetching && <LoadingOverlay />}
        </div>
      )
}

export default MailContentList
